import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Trash2, Plus } from 'lucide-react';
import { Condition, CanvasElement, ConditionalPosition } from './types';
import { CustomNumberInput } from './CustomNumberInput';

interface ConditionalYModalProps {
  visible: boolean;
  onHide: () => void;
  element: CanvasElement;
  onSave: (conditionalY: ConditionalPosition | undefined) => void;
  variables: any[];
}

export const ConditionalYModal: React.FC<ConditionalYModalProps> = ({
  visible,
  onHide,
  element,
  onSave,
  variables
}) => {
  const [conditions, setConditions] = React.useState<Condition[]>(element.conditionalY?.conditions || []);
  const [logic, setLogic] = React.useState<'AND' | 'OR'>(element.conditionalY?.conditionLogic || 'AND');
  const [yPosition, setYPosition] = React.useState<number>(element.conditionalY?.y || element.y);

  React.useEffect(() => {
    if (visible) {
      setConditions(element.conditionalY?.conditions || []);
      setLogic(element.conditionalY?.conditionLogic || 'AND');
      setYPosition(element.conditionalY?.y !== undefined ? element.conditionalY.y : element.y);
    }
  }, [visible, element]);

  const operators = [
    { label: 'Eşittir (==)', value: 'equals' },
    { label: 'Eşit Değildir (!=)', value: 'notEquals' },
    { label: 'İle Başlar', value: 'startsWith' },
    { label: 'İçerir', value: 'contains' },
    { label: 'Boştur', value: 'isEmpty' },
    { label: 'Dolu', value: 'isNotEmpty' },
    { label: 'SKU İçerir (Liste)', value: 'hasItemWithSku' }
  ];

  const handleAdd = () => {
    setConditions([...conditions, { field: '', operator: 'equals', value: '' }]);
  };

  const handleUpdate = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setConditions(newConditions);
  };

  const handleDelete = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
  };

  const handleSave = () => {
    const valid = conditions.filter(c => c.field && c.operator);
    if (valid.length > 0) {
      onSave({ conditions: valid, conditionLogic: logic, y: yPosition });
    } else {
      onSave(undefined);
    }
    onHide();
  };

  const handleRemoveAll = () => {
    onSave(undefined);
    onHide();
  };

  return (
    <Dialog header="Koşullu Başlangıç (Y)" visible={visible} style={{ width: '600px' }} onHide={onHide}
      footer={
        <div className="flex justify-between items-center w-full">
            <Button label="Koşulları Temizle" icon={<Trash2 className="w-4 h-4 mr-2"/>} onClick={handleRemoveAll} className="p-button-danger p-button-text" disabled={conditions.length === 0} />
            <div className="flex justify-end gap-2">
                <Button label="İptal" icon="pi pi-times" onClick={onHide} className="p-button-text" />
                <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} autoFocus />
            </div>
        </div>
      }>
      <p className="m-0 mb-4 text-sm text-text/70">
        Bu nesnenin Y koordinatının belirli bir koşul sağlandığında farklı bir değer almasını sağlayabilirsiniz. (Örn: Sipariş notu boşsa tabloyu 10mm daha yukarıdan başlat).
      </p>

      <div className="mb-4 flex flex-col gap-2 p-3 bg-primary/10 border border-primary/20 rounded">
        <label className="text-sm font-semibold text-text">Yeni Y Konumu (mm):</label>
        <div className="w-1/2">
            <CustomNumberInput
                value={yPosition}
                onChange={(val) => setYPosition(val || 0)}
            />
        </div>
        <span className="text-xs text-text/70">Koşullar sağlandığında nesne bu Y konumundan başlayacaktır.</span>
      </div>

      {conditions.length > 1 && (
        <div className="mb-4 flex items-center gap-2">
          <label className="text-sm font-semibold text-text">Koşul Mantığı:</label>
          <Dropdown 
            value={logic} 
            options={[{label: 'Tümü Sağlanmalı (AND)', value: 'AND'}, {label: 'Herhangi Biri Sağlanmalı (OR)', value: 'OR'}]} 
            onChange={(e) => setLogic(e.value)} 
            className="w-48"
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        {conditions.map((cond, index) => (
          <div key={index} className="flex items-center gap-2 bg-bgAlt p-2 rounded border border-borderColor">
            <InputText 
              placeholder="Alan (örn: order.note)" 
              value={cond.field} 
              onChange={(e) => handleUpdate(index, { field: e.target.value })} 
              className="p-inputtext-sm flex-1 w-full"
            />
            <Dropdown 
              value={cond.operator} 
              options={operators} 
              onChange={(e) => handleUpdate(index, { operator: e.value })} 
              className="p-dropdown-sm w-48 shrink-0"
            />
            {cond.operator !== 'isEmpty' && cond.operator !== 'isNotEmpty' && (
               <InputText 
                 placeholder="Değer" 
                 value={cond.value || ''} 
                 onChange={(e) => handleUpdate(index, { value: e.target.value })} 
                 className="p-inputtext-sm flex-1 w-full"
               />
            )}
            <Button icon={<Trash2 className="w-4 h-4"/>} className="p-button-danger p-button-text p-2 hover:bg-danger/10 text-danger" onClick={() => handleDelete(index)} />
          </div>
        ))}

        <Button label="Koşul Ekle" icon={<Plus className="w-4 h-4 mr-2"/>} className="p-button-outlined p-button-sm w-32 mt-2" onClick={handleAdd} />
      </div>
    </Dialog>
  );
};
